// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
import Switch from "@mui/material/Switch";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    icon?: string;
    title: string;
    toggle?: {
        checked: boolean;
        onChange: (checked: boolean) => void;
    };
    addButton?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
    /** Compact round action sitting just before the add button — icon only, label is the tooltip. */
    iconButton?: {
        icon: string;
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
    children?: ReactNode;
};

// --- Constants ----------------------------------------------------------------------------------

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Logic --------------------------------------------------------------------------------------

export default function InputGroups({ icon, title, toggle, addButton, iconButton, children }: Props) {
    return (
        <div className="input-groups">
            <div className="input-groups-header">
                {icon && (
                    <div className="input-groups-icon">
                        <img src={icon} alt="" width={17} height={17} />
                    </div>
                )}
                <span className="input-groups-title">{title}</span>
                {toggle && <Switch checked={toggle.checked} onChange={(e) => toggle.onChange(e.target.checked)} sx={switchSx} />}
                {iconButton && (
                    <button
                        type="button"
                        className="input-groups-icon-btn"
                        onClick={iconButton.onClick}
                        disabled={iconButton.disabled}
                        title={iconButton.label}
                        aria-label={iconButton.label}
                    >
                        <img src={iconButton.icon} alt="" width={16} height={16} />
                    </button>
                )}
                {addButton && (
                    <button type="button" className="input-groups-add" onClick={addButton.onClick} disabled={addButton.disabled}>
                        <img src="/icons/plus-icon.svg" alt="" width={12} height={12} />
                        <span>{addButton.label}</span>
                    </button>
                )}
            </div>
            {children && <div className="input-groups-content">{children}</div>}
        </div>
    );
}
